import React, { useState, useEffect } from 'react';
import { Database, Plus, X } from 'lucide-react';
import {
  fetchAllResources,
  createResource,
  updateResourceCapacity,
  updateResourceName,
  deleteResource,
} from '../services/resourceService';
import ActionDropdown from './ActionDropdown';
import ConfirmDialog from './ConfirmDialog';

const ResourceManagementTable = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newResource, setNewResource] = useState({ name: '', capacity: '' });
  const [editingResource, setEditingResource] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const resourcesData = await fetchAllResources();
      setResources(resourcesData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateResource = async (e) => {
    e.preventDefault();

    if (!newResource.name || !newResource.capacity) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await createResource(newResource.name, parseInt(newResource.capacity));
      setNewResource({ name: '', capacity: '' });
      setShowCreateForm(false);
      await loadData();
    } catch (err) {
      console.error('Error creating resource:', err);
      alert('Failed to create resource. Please try again.');
    }
  };

  const handleUpdateCapacity = async (resourceId, newCapacity) => {
    if (!newCapacity || isNaN(newCapacity)) {
      alert('Please enter a valid capacity');
      return;
    }

    // Optimistic update
    const originalResources = [...resources];
    setResources(
      resources.map((r) =>
        r.id === resourceId ? { ...r, capacity: parseInt(newCapacity) } : r
      )
    );

    try {
      await updateResourceCapacity(resourceId, parseInt(newCapacity));
      await loadData();
    } catch (err) {
      console.error('Error updating capacity:', err);
      setResources(originalResources);
      alert('Failed to update capacity. Please try again.');
    }
  };

  const handleUpdateName = async (resourceId, newName) => {
    if (!newName) {
      alert('Please enter a valid name');
      return;
    }

    // Optimistic update
    const originalResources = [...resources];
    setResources(
      resources.map((r) => (r.id === resourceId ? { ...r, name: newName } : r))
    );

    try {
      await updateResourceName(resourceId, newName);
      await loadData();
    } catch (err) {
      console.error('Error updating name:', err);
      setResources(originalResources);
      alert('Failed to update name. Please try again.');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Resource',
      message:
        'Are you sure you want to delete this resource? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false });

        // Optimistic update
        const originalResources = [...resources];
        setResources(resources.filter((r) => r.id !== resourceId));

        try {
          await deleteResource(resourceId);
          await loadData();
        } catch (err) {
          console.error('Error deleting resource:', err);
          setResources(originalResources);
          alert('Failed to delete resource. Please try again.');
        }
      },
      onCancel: () => {
        setConfirmDialog({ isOpen: false });
      },
    });
  };

  const startEditingCapacity = (resource) => {
    const newCapacity = prompt('Enter new capacity:', resource.capacity);
    if (newCapacity !== null) {
      handleUpdateCapacity(resource.id, newCapacity);
    }
  };

  const startEditingName = (resource) => {
    const newName = prompt('Enter new name:', resource.name);
    if (newName !== null) {
      handleUpdateName(resource.id, newName);
    }
  };

  const getActions = (resource) => [
    {
      label: 'Change Capacity',
      onClick: () => startEditingCapacity(resource),
    },
    {
      label: 'Rename',
      onClick: () => startEditingName(resource),
    },
    {
      label: 'Delete',
      onClick: () => handleDeleteResource(resource.id),
      variant: 'danger',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <h3 className="text-red-800 font-semibold mb-2">
            Error loading resources
          </h3>
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        {!showCreateForm ? (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Resource
          </button>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                Create New Resource
              </h3>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewResource({ name: '', capacity: '' });
                }}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCreateResource} className="flex gap-3">
              <input
                type="text"
                placeholder="Resource name"
                value={newResource.name}
                onChange={(e) =>
                  setNewResource({ ...newResource, name: e.target.value })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="number"
                placeholder="Capacity"
                value={newResource.capacity}
                onChange={(e) =>
                  setNewResource({ ...newResource, capacity: e.target.value })
                }
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Create
              </button>
            </form>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-6 font-medium text-gray-900">
                  Name
                </th>
                <th className="text-left py-3 px-6 font-medium text-gray-900">
                  Capacity
                </th>
                <th className="text-right py-3 px-6 font-medium text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {resources.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-8 text-gray-500">
                    <Database className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No resources found</p>
                  </td>
                </tr>
              ) : (
                resources.map((resource) => (
                  <tr
                    key={resource.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-lg flex items-center justify-center text-white">
                          <Database className="w-5 h-5" />
                        </div>
                        <span className="font-medium text-gray-900">
                          {resource.name}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-900">{resource.capacity}</span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end">
                        <ActionDropdown actions={getActions(resource)} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
      />
    </>
  );
};

export default ResourceManagementTable;
